import React, { FC, useState, useEffect } from 'react';
import { useTranslate } from '@im/react-localization';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Modal from 'src/components/Modal';
import { Switch } from 'src/components/Switch';
import { CustomerGroup as CustomerGroupDetails } from '../hooks/customerGroupSearchQuery';
import { useCustomerGroupUpdate } from '../hooks/customerGroupMutations';
import EditCustomerGroupForm from './EditCustomerGroupForm';
import DetailField from './DetailField';
import { useRoles } from 'src/provider/UserRoleProvider';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

function useToggleGroupActivation(
  customerGroup: CustomerGroupDetails,
  isProductGroups?: boolean
) {
  const [active, setActive] = useState<boolean>(!!customerGroup.status);
  const {
    error,
    isError,
    mutate,
    reset: resetMutation,
  } = useCustomerGroupUpdate(isProductGroups);
  useEffect(() => {
    setActive(!!customerGroup.status);
  }, [customerGroup, isError]);

  function toggleActivation() {
    mutate({
      description: customerGroup.description,
      id: customerGroup.id,
      status: !active,
    });
  }

  return { active, error, resetMutation, toggleActivation };
}

export interface CustomerGroupDetailsInfoProps {
  customerGroup: CustomerGroupDetails;
  isProductGroups?: boolean;
}

const CustomerGroupDetailsInfo: FC<CustomerGroupDetailsInfoProps> = ({
  customerGroup,
  isProductGroups,
}) => {
  const { description, id, lastUpdated, name, status } = customerGroup;

  const translate = useTranslate();
  const { isCOGUser, isVBMUser } = useRoles();
  const formatDateTime = useDateTimeFormatter();
  const { active, error, resetMutation, toggleActivation } =
    useToggleGroupActivation(customerGroup, isProductGroups);
  const onClickTrigger = ({ onClick }: { onClick(result: any): void }) => (
    <IconButton
      onClick={onClick}
      size="small"
      sx={(theme) => ({
        display: isCOGUser || isVBMUser ? 'unset' : 'none',
        marginLeft: theme.spacing(0.75),
      })}
    >
      <ModeEditOutlinedIcon
        fontSize="small"
        sx={(theme) => ({
          color: theme.palette.primary.main,
        })}
      />
    </IconButton>
  );
  return (
    <Stack alignItems="start" direction="row" justifyContent="space-between">
      <Stack>
        <Typography fontSize={22} m={0} textAlign={'left'} variant="h2">
          {name}
          <Modal
            notification={() =>
              translate('CustomerGroups.updateGroupSuccessNotification')
            }
            trigger={onClickTrigger}
          >
            {({ onClose }) => (
              <EditCustomerGroupForm
                customerGroup={{
                  description,
                  id,
                  name,
                  status,
                }}
                isProductGroups={isProductGroups}
                onClose={onClose}
              />
            )}
          </Modal>
        </Typography>
        <Typography
          mb={1.75}
          mt={1}
          sx={(theme) => ({
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
          })}
        >
          {description}
        </Typography>
      </Stack>
      <Stack
        alignItems="center"
        direction="row"
        divider={<Divider flexItem orientation="vertical" />}
        spacing={2}
      >
        <FormGroup>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Switch
              checked={active}
              disabled={!isCOGUser && !isVBMUser}
              labelChecked={translate('CustomerGroups.active')}
              onChange={toggleActivation}
            />
            <Snackbar
              anchorOrigin={{
                horizontal: 'center',
                vertical: 'top',
              }}
              autoHideDuration={6000}
              onClose={resetMutation}
              open={!!error}
            >
              <Alert severity="error">
                {translate('CustomerGroups.editGroupErrorNotification')}
              </Alert>
            </Snackbar>
          </Stack>
        </FormGroup>
        <DetailField
          label={translate('CustomerGroups.id')}
          value={id.toString()}
        />
        <DetailField
          label={translate('CustomerGroups.updated')}
          value={formatDateTime(lastUpdated)}
        />
      </Stack>
    </Stack>
  );
};

export default CustomerGroupDetailsInfo;
